import { DG } from '@cotera/sdk/core';
import { Routes } from '../routes';
import { BaseModel } from '../actions/framework';
import { ReadonlyWatchable, Watchable } from '../actions/framework/watchable';
import {
  StorageConnection,
  UserBranchesConnection,
} from '../models/user-branches';
import { ModelResource } from '../actions/framework/types';
import {
  BuildExplorer,
  GraphCompilerResult,
} from 'libs/cotera-sdk/src/core/dg/commit';
import { GRAPH_EVENTS } from './constants';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace GraphModel {
  export type Props = {
    userId: string;
    commits: DG.SavedCommit[];
    org: { id: string; name: string };
    storage: StorageConnection;
  };
}

/**
 * Warnings:
 *  - Any updates to userBranches should be accompanied by synchronous updates to the stagedOperations watchable cache.
 */
export class GraphModel extends BaseModel {
  private readonly userId: string;

  /**
   * An asynchronous connection to user branches data.
   */
  readonly userBranches: UserBranchesConnection;

  /**
   * A synchronous cache of userBranches' staged operations of the current branch.
   */
  readonly stagedOperations: Watchable<DG.OperationData[]>;

  /**
   * The client-side representation of currently-published commits on the server.
   *
   * May potentially be momentarily invalid if another user publishes a commit before
   * this field is refreshed (or "rebased").
   */
  readonly publishedCommits: Watchable<DG.SavedCommit[]>;

  private readonly _autoPublish: Watchable<boolean>;

  /**
   * The compiled graph of everything:
   *  - the published commits
   *  - the staged operations
   */
  readonly graph: ReadonlyWatchable<DG.BuildExplorer>;

  /**
   * A compiler, loaded with everything:
   *  - the published commits
   *  - the staged operations
   */
  readonly compiler: ReadonlyWatchable<DG.GraphCompiler>;

  readonly hasUnpublishedOperations: ReadonlyWatchable<boolean>;

  /**
   * If the staged operations compile (to-be-published) compile.
   */
  readonly publishable: ReadonlyWatchable<boolean>;

  private disposeStagedOperationsListener: () => void;

  private static constructorKey = Symbol();

  /**
   *
   * @param userId
   * @param initiallyPublishedCommits The server-side-published commits at time of instantiation.
   * @param userBranches
   * @param stagedOperations
   * @param constructorKey
   */
  constructor(
    userId: string,
    initiallyPublishedCommits: DG.SavedCommit[],
    userBranches: UserBranchesConnection,
    stagedOperations: DG.OperationData[],
    autoPublish: boolean,
    private readonly storage: StorageConnection,
    private readonly eventEmitter: {
      dispatch: (event: CustomEvent) => void;
    },
    private readonly toolResolver: DG.Graph.IToolResolver,
    constructorKey: symbol
  ) {
    super('graph');

    if (constructorKey !== GraphModel.constructorKey) {
      throw new Error(
        'cannot instantiate via constructor. use GraphModel.create()'
      );
    }
    this.userId = userId;
    this.publishedCommits = Watchable.fromValue(initiallyPublishedCommits);
    this.userBranches = userBranches;
    this.stagedOperations = Watchable.fromValue(stagedOperations);

    this.compiler = Watchable.from((get) => {
      const tempCommit: DG.Commit = {
        actorId: this.userId,
        message: null,
        operations: [...get(this.stagedOperations)],
        timestamp: new Date(),
      };

      const compiler = DG.GraphCompiler.fromCommits(
        [...get(this.publishedCommits), tempCommit],
        this.toolResolver
      );

      return compiler;
    });

    this.graph = Watchable.from((get) => {
      const compiler = get(this.compiler);

      const graph = compiler.compile().match(
        (x) => x,
        (x) => x.lastGoodBuild
      );

      return graph;
    });

    this._autoPublish = Watchable.fromValue(autoPublish);

    this.publishable = Watchable.from((get) => {
      return get(this.graph).allItemsAreValid();
    });

    this.hasUnpublishedOperations = Watchable.from((get) => {
      return get(this.stagedOperations).length > 0;
    });
  }

  /**
   * Creates a GraphModel instance.
   */
  static async create(props: {
    userId: string;
    commits: DG.SavedCommit[];
    org: { id: string; name: string };
    storage: StorageConnection;
    userBranches: UserBranchesConnection;
    eventEmitter: {
      dispatch: (event: CustomEvent) => void;
    };
    toolResolver: DG.Graph.IToolResolver;
  }) {
    return new GraphModel(
      props.userId,
      props.commits,
      props.userBranches,
      await props.userBranches.getStagedOperations(),
      await props.storage.getAutoPublish(),
      props.storage,
      props.eventEmitter,
      props.toolResolver,
      GraphModel.constructorKey
    );
  }

  onCreate() {
    this.disposeStagedOperationsListener = this.stagedOperations.subscribe(
      (ops) => {
        this.eventEmitter.dispatch(
          new CustomEvent(GRAPH_EVENTS.OPS_ADD_TO_STAGED, { detail: { ops } })
        );
      }
    );
  }

  get autoPublish(): ReadonlyWatchable<boolean> {
    return this._autoPublish;
  }

  async setAutoPublish(value: boolean): Promise<void> {
    this._autoPublish.set(value);
    await this.storage.setAutoPublish(value);
  }

  async attemptToStageOperations(
    operations: DG.OperationData[]
  ): Promise<GraphCompilerResult> {
    const tempCommit: DG.Commit = {
      actorId: this.userId,
      message: null,
      operations: [
        ...(await this.userBranches.getStagedOperations()),
        ...operations,
      ],
      timestamp: new Date(),
    };

    const compiler = DG.GraphCompiler.fromCommits(
      [...this.publishedCommits.snapshot(), tempCommit],
      this.toolResolver
    );

    const compilationResult = compiler.compile();

    if (compilationResult.isOk()) {
      const _: void = await this.userBranches.addStagedOperations(operations);
      this.stagedOperations.set(await this.userBranches.getStagedOperations());
    }

    return compilationResult;
  }

  refreshPublishedCommitsCache(freshPublishedCommits: DG.SavedCommit[]) {
    // TODO Verify that the current cache doesn't have anything that the fresh commits doesn't have.
    this.publishedCommits.set(freshPublishedCommits);
  }

  resources(): ModelResource[] {
    return getGraphResources(this.graph.snapshot());
  }

  /**
   * Marks the given commits as published and updates the local cache.
   *
   * Note: The commits' indices may differ from the optimistic indices sent to the server
   * if conflicts occurred. The server resolves conflicts by assigning the next available
   * index, and those actual indices are returned here and stored in the cache.
   *
   * @param commits The commits returned from the server, with their actual assigned indices
   */
  async markAsPublished(commits: DG.SavedCommit[]) {
    this.stagedOperations.set([]);
    await this.userBranches.clearStagedOperations();
    this.publishedCommits.set([
      ...this.publishedCommits.snapshot(),
      ...commits,
    ]);
  }

  dispose() {
    this.disposeStagedOperationsListener();
  }
}

/**
 * Returns the resources of the graphModel data. (to appear on the command palette
 * resources section).
 */
function getGraphResources(graph: BuildExplorer): ModelResource[] {
  return [
    ...Object.entries(graph.nodeMap)
      .map<ModelResource[] | null>(([name, node]) => {
        if (node === null) {
          return null;
        }
        switch (node.t) {
          case 'rel':
            return [
              {
                t: 'tab',
                icon: 'link',
                group: 'Datasets',
                name,
                tabRoute: Routes.dataset.forId(name).toString(),
              },
              ...Object.entries(node.cols).map<ModelResource>(([colName]) => {
                return {
                  t: 'tab',
                  icon: 'link',
                  group: 'Columns',
                  name: colName,
                  description: name,
                  tabRoute: Routes.dataset
                    .forId(name)
                    .column.edit(name, colName)
                    .toString(),
                };
              }),
            ];
          case 'agent':
            return [
              {
                t: 'tab',
                icon: 'sparkles',
                group: 'Agents',
                name,
                tabRoute: Routes.agent.forId(name).toString(),
              },
            ];
          default:
            return null;
        }
      })
      .filter((x) => x !== null)
      .flat(),
    {
      t: 'tab',
      icon: 'document-magnifying-glass',
      group: 'Graph',
      name: 'Change Log',
      tabRoute: Routes.static.operations,
    },
    {
      t: 'tab',
      icon: 'finger-print',
      group: 'Graph',
      name: 'Identities',
      tabRoute: Routes.static.identities,
    },
    {
      t: 'tab',
      icon: 'activity',
      group: 'Graph',
      name: 'Operations Monitoring',
      tabRoute: Routes.runs.forKey('workflow-program-row').toString(),
      priority: 1,
    },
  ];
}
